/**
 * ReportTable Component
 *
 * Print-optimized table component for reports.
 * Supports sorting, grouping, and responsive column hiding.
 */

import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { Loader2 } from 'lucide-react';

export interface ReportTableColumn<T> {
  key: string;
  header: string;
  render?: (row: T, index: number) => React.ReactNode;
  align?: 'left' | 'center' | 'right';
  width?: string;
  className?: string;
  hideOnPrint?: boolean;
  hideOnMobile?: boolean;
}

interface ReportTableProps<T> {
  data: T[];
  columns: ReportTableColumn<T>[];
  keyExtractor: (row: T, index: number) => string;
  emptyMessage?: string;
  emptyIcon?: React.ReactNode;
  loading?: boolean;
  className?: string;
  sectionHeader?: string;
}

export function ReportTable<T>({
  data,
  columns,
  keyExtractor,
  emptyMessage = 'No data available',
  emptyIcon,
  loading = false,
  className,
  sectionHeader,
}: ReportTableProps<T>) {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const getAlignmentClass = (align?: 'left' | 'center' | 'right') => {
    switch (align) {
      case 'center':
        return 'text-center';
      case 'right':
        return 'text-right';
      default:
        return 'text-left';
    }
  };

  return (
    <div className={cn('report-table-wrapper', className)}>
      {sectionHeader && (
        <h3 className="report-section-header text-lg font-semibold mb-4 pb-2 border-b-2 border-primary print:text-sm print:mb-2">
          {sectionHeader}
        </h3>
      )}

      <div className="rounded-md border print:border-0">
        <Table className="report-table">
          <TableHeader>
            <TableRow className="print:bg-gray-100">
              {columns.map(column => (
                <TableHead
                  key={column.key}
                  className={cn(
                    getAlignmentClass(column.align),
                    column.width && `w-[${column.width}]`,
                    column.hideOnPrint && 'print:hidden',
                    column.hideOnMobile && 'hidden md:table-cell',
                    'print:text-[9pt] print:py-1 print:px-2',
                    column.className
                  )}
                >
                  {column.header}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-32 text-center"
                >
                  <div className="flex flex-col items-center gap-2 text-muted-foreground">
                    {emptyIcon}
                    <p>{emptyMessage}</p>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              data.map((row, index) => (
                <TableRow
                  key={keyExtractor(row, index)}
                  className="print:even:bg-gray-50"
                >
                  {columns.map(column => (
                    <TableCell
                      key={column.key}
                      className={cn(
                        getAlignmentClass(column.align),
                        column.hideOnPrint && 'print:hidden',
                        column.hideOnMobile && 'hidden md:table-cell',
                        'print:text-[9pt] print:py-1 print:px-2',
                        column.className
                      )}
                    >
                      {column.render
                        ? column.render(row, index)
                        : String((row as any)[column.key] ?? '-')}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

/**
 * ReportSection Component
 *
 * Wrapper for report sections with page break handling.
 */
interface ReportSectionProps {
  children: React.ReactNode;
  title?: string;
  className?: string;
  avoidPageBreak?: boolean;
}

export function ReportSection({
  children,
  title,
  className,
  avoidPageBreak = false,
}: ReportSectionProps) {
  return (
    <div
      className={cn(
        'report-section',
        avoidPageBreak && 'print:break-inside-avoid',
        className
      )}
    >
      {title && (
        <h3 className="report-section-header text-lg font-semibold mb-4 pb-2 border-b print:text-sm print:mb-2 print:border-b-2 print:border-primary">
          {title}
        </h3>
      )}
      {children}
    </div>
  );
}
